import { v4 as uuidv4 } from "uuid";

import { customFetch } from "./customFetch";
import { FetchResponseError, HTTP_METHOD, withFullResponseSymbol } from "./defaults";
import { getHttpHeaders } from "./httpHeaders";
import type {
    CommonRequestOptions,
    ConfigObject,
    ErrorProps,
    FetchParam,
    ForceResultWithFullResponse,
    GetRequestOptions,
    IFetchService,
    IRequestOpts,
    PayloadParam,
    PostConfigObject,
    PutConfigObject,
    RequestInterceptor,
    RequestParams,
    ResultWithFullResponse,
} from "./types";
import { getResponseError } from "./utils";

const DEFAULT_LATEST_RANGE = "28d";

export const checkResultWithFullResponse = <T>(resultObj: T): boolean =>
    (resultObj as Record<symbol, boolean> | null | undefined)?.[withFullResponseSymbol] ?? false;

let pendingRequestsAbortControllers: Record<string, AbortController> = {};

export const abortAllPendingRequests = () => {
    Object.values(pendingRequestsAbortControllers).forEach((ctrl) =>
        ctrl.abort(new DOMException("Aborting pending request due to route change", "AbortError")),
    );
    pendingRequestsAbortControllers = {};
};

// create a single abort controller for multiple signals
const createMultiAbortController = (signals: AbortSignal[]) => {
    const controller = new AbortController();
    function onAbort() {
        controller.abort();
        // Cleanup
        for (const signal of signals) {
            signal.removeEventListener("abort", onAbort);
        }
    }

    for (const signal of signals) {
        if (signal.aborted) {
            onAbort();
            break;
        }
        signal.addEventListener("abort", onAbort);
    }
    return controller;
};

const multiAbortSignalMixin = (signal: AbortSignal) => {
    const controller = new AbortController();
    return createMultiAbortController([signal, controller.signal]);
};

const handleError = async (errorProps: ErrorProps) => {
    const { err, numberOfRetry, withFullResponse, realUrl, isPostRequest, payload } = errorProps;
    // Regardless the amount of retires report only once after the first failure
    if (numberOfRetry === 1) {
        if (withFullResponse) {
            throw err;
        }

        throw await getResponseError({
            body: payload,
            error: err,
            method: isPostRequest ? HTTP_METHOD.post : HTTP_METHOD.get,
            url: realUrl,
        });
    }
};

export const requestParams = <T extends FetchParam>(params: T): string => {
    const overrideParams = { ...params };

    // If the overrideParams object has a property isWindow set to true, then we need to set the latest to 28d
    // Don't override if latest is already set
    if (overrideParams.isWindow && !overrideParams.latest) {
        (overrideParams.latest as keyof T) = DEFAULT_LATEST_RANGE;
    }

    if (overrideParams && typeof overrideParams === "object" && !Array.isArray(overrideParams)) {
        // Clear out fields that are undefined | null | empty string
        Object.keys(overrideParams).forEach((key) => {
            if (overrideParams[key] === undefined || overrideParams[key] === null || overrideParams[key] === "") {
                delete overrideParams[key];
            }
        });
    }

    return new URLSearchParams(overrideParams).toString();
};

export abstract class BaseFetchService<R> implements IFetchService {
    public async get<T>(url: string, params?: FetchParam | null, config: GetRequestOptions = {}): Promise<T> {
        return this.send<T>(url, {
            method: HTTP_METHOD.get,
            params,
            cancellation: new AbortController().signal,
            numberOfRetry: 1,
            ...config,
        });
    }

    public async post<T>(url: string, payload?: PayloadParam, config?: PostConfigObject): Promise<T> {
        return this.send<T>(url, {
            method: HTTP_METHOD.post,
            payload,
            ...config,
        });
    }

    public async put<T>(url: string, payload?: PayloadParam, config?: PutConfigObject): Promise<T> {
        return this.send<T>(url, {
            method: HTTP_METHOD.put,
            payload,
            ...config,
        });
    }

    public async delete<T>(
        url: string,
        params?: FetchParam,
        payload?: PayloadParam,
        config?: CommonRequestOptions,
    ): Promise<T> {
        return this.send<T>(url, {
            method: HTTP_METHOD.delete,
            params,
            payload,
            ...config,
        });
    }

    public async patch<T>(url: string, payload: PayloadParam, config?: PutConfigObject): Promise<T> {
        return this.send<T>(url, {
            method: HTTP_METHOD.patch,
            payload,
            ...config,
        });
    }

    public async send<T>(url: string, config: ConfigObject): Promise<T> {
        const {
            method,
            bodyParams,
            params,
            payload,
            cancellation,
            preventAutoCancellation,
            numberOfRetry = 1,
            headers,
            credentials,
            withFullResponse = false,
            isRawResponse = false,
            timeout,
            ...other
        } = config;

        const execOpts = {
            withFullResponse,
            isRawResponse,
        };

        let realUrl = url;
        if (params) {
            realUrl = `${url}?${this.requestParams(params)}`;
        }

        const requestInit: IRequestOpts = {
            method,
            headers,
            credentials,
            ...other,
        };

        switch (requestInit.method) {
            case HTTP_METHOD.put:
            case HTTP_METHOD.patch:
                requestInit.body = JSON.stringify(payload);
                return this.executeRequest<T>(realUrl, requestInit, execOpts);

            case HTTP_METHOD.get: {
                const requestId = uuidv4();
                let timeoutRef: NodeJS.Timeout | undefined;
                if (cancellation) {
                    // if the request contains abort signal and doesn't prevent auto abort
                    if (!preventAutoCancellation) {
                        const multiSignal = multiAbortSignalMixin(cancellation);
                        requestInit.signal = multiSignal.signal;
                        pendingRequestsAbortControllers[requestId] = multiSignal;
                        // if the request contains abort signal and prevent auto abort
                    } else {
                        requestInit.signal = cancellation;
                    }
                    // if the request doesn't contain abort signal and doesn't prevent auto abort
                } else if (!preventAutoCancellation) {
                    const controller = new AbortController();
                    requestInit.signal = controller.signal;
                    pendingRequestsAbortControllers[requestId] = controller;
                    if (timeout) {
                        timeoutRef = setTimeout(() => {
                            controller?.abort();
                            delete pendingRequestsAbortControllers[requestId];
                        }, timeout);
                    }
                }

                try {
                    const response = await this.executeRequest<T>(realUrl, requestInit, execOpts);
                    if (timeoutRef) {
                        clearTimeout(timeoutRef);
                    }
                    return response;
                } catch (err: unknown) {
                    await handleError({
                        err: err as FetchResponseError,
                        numberOfRetry,
                        withFullResponse,
                        realUrl,
                        isPostRequest: false,
                    });
                    return await this.get<T>(url, params, {
                        headers,
                        preventAutoCancellation,
                        cancellation,
                        numberOfRetry: numberOfRetry - 1,
                    });
                } finally {
                    delete pendingRequestsAbortControllers[requestId];
                }
            }
            case HTTP_METHOD.post:
                if (cancellation) {
                    requestInit.signal = cancellation;
                }
                requestInit.body = JSON.stringify(payload);

                try {
                    return await this.executeRequest<T>(realUrl, requestInit, execOpts);
                } catch (err: unknown) {
                    await handleError({
                        err: err as FetchResponseError,
                        numberOfRetry,
                        withFullResponse,
                        realUrl,
                        isPostRequest: true,
                        payload,
                    });
                    return await this.post<T>(realUrl, payload, {
                        headers,
                        credentials,
                        numberOfRetry: numberOfRetry - 1,
                    });
                }

            case HTTP_METHOD.delete:
                if (payload) {
                    requestInit.body = JSON.stringify(payload);
                }
                return this.executeRequest<T>(realUrl, requestInit, execOpts);

            default: {
                const _exhaustive: never = requestInit.method as never;
                throw new Error(`Unsupported HTTP method: ${requestInit.method}`);
            }
        }
    }

    /**
     * returns a string error if failed
     * @param {R} result
     * @returns {string}
     */
    protected abstract validate(result: R): string;

    /**
     * transform result
     * @param {R} result
     * @returns {T}
     */
    protected abstract transform(result: R): unknown;

    private async executeRequestFull<T>(
        url: string,
        requestOpts: IRequestOpts,
        opts?: { withFullResponse?: boolean; isRawResponse?: boolean },
    ): Promise<ForceResultWithFullResponse<T>> {
        requestOpts.headers = {
            ...getHttpHeaders(),
            ...requestOpts.headers,
        };
        requestOpts.credentials = requestOpts.credentials ?? "same-origin";

        const request = this.applyInterceptors({ url, options: requestOpts });

        const response = await customFetch(request.url, request.options);

        if (response.status === 204 || response.headers.get("content-length") === "0") {
            return {
                response,
                result: null,
                [withFullResponseSymbol]: true,
            } as ForceResultWithFullResponse<T>;
        }

        if (opts?.isRawResponse) {
            return {
                response,
                result: response.body,
                [withFullResponseSymbol]: true,
            } as ForceResultWithFullResponse<T>;
        }

        let result: R;
        // We need to clone the response to be able to read it multiple times
        // because the response body is a stream and can be read only once
        // This part is only a safety measure, in case line 402 returns an error
        const responseClone = response.clone();

        try {
            result = await responseClone.json();
        } catch (e: unknown) {
            // TODO: remove signal from activeAbortSignals list
            throw new FetchResponseError(e instanceof Error ? e.message : String(e), response, withFullResponseSymbol);
        }

        const error = this.validate(result);
        // TODO: remove signal from activeAbortSignals list
        if (error) {
            throw new FetchResponseError(error, response, withFullResponseSymbol);
        }

        return {
            response,
            result: this.transform(result),
            [withFullResponseSymbol]: true,
        } as ForceResultWithFullResponse<T>;
    }

    private async executeRequest<T>(
        url: string,
        requestOpts: IRequestOpts,
        opts?: { withFullResponse?: boolean; isRawResponse?: boolean },
    ): Promise<T> {
        const { withFullResponse = false } = opts ?? {};
        const fullExecValue = await this.executeRequestFull<T>(url, requestOpts, opts);
        return withFullResponse ? (fullExecValue as T) : (fullExecValue as ResultWithFullResponse<T>).result;
    }

    public requestParams = requestParams;

    private requestInterceptors: RequestInterceptor[] = [];

    public registerRequestInterceptor(interceptor: RequestInterceptor) {
        this.requestInterceptors.push(interceptor);
    }

    public unregisterRequestInterceptor(interceptorToRemove: RequestInterceptor) {
        this.requestInterceptors = this.requestInterceptors.filter(
            (interceptor) => interceptor !== interceptorToRemove,
        );
    }

    private applyInterceptors(ApplyRequestParams: RequestParams): RequestParams {
        return this.requestInterceptors.reduce(
            (currentPrams, interceptor) => interceptor(currentPrams),
            ApplyRequestParams,
        );
    }
}
